import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Archive, Bell, BellOff, Download, FolderInput, FolderPlus, Search, Trash2, UploadCloud, X as XIcon } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useWorkspaces } from '@/api/workspaces';
import {
  useCreateFolder,
  useDeleteFolder,
  useFolderActivity,
  useFolderSubscription,
  useFolders,
  useMoveFolder,
  useReorderFolder,
  useSubscribeFolder,
  useUnsubscribeFolder,
} from '@/api/folders';
import {
  uploadFile,
  useFiles,
  useMoveFile,
  useSoftDeleteFile,
  useRestoreFile,
  downloadFile,
  downloadFolderZip,
  useBulkDeleteFiles,
  useBulkArchiveFiles,
} from '@/api/files';
import { FolderTree, type FolderDragData } from '@/components/folders/FolderTree';
import type { FileDragData } from '@/components/files/FileRow';
import { FileTable } from '@/components/files/FileTable';
import { BulkMoveModal } from '@/components/files/BulkMoveModal';
import { RenameFileModal } from '@/components/files/RenameFileModal';
import { MoveFileModal } from '@/components/files/MoveFileModal';
import { FolderBreadcrumb } from '@/components/folders/FolderBreadcrumb';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useIsClient } from '@/hooks/useIsClient';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useOrganization } from '@/api/organizations';
import { useUIStore } from '@/stores/ui';
import { apiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { FileItem, Folder } from '@/types/api';

export function FolderPage() {
  const isClient = useIsClient();
  const { hasRole } = useAuth();
  const canManageFolders = hasRole(['sysadmin', 'manager', 'staff', 'client_admin', 'client_user']);
  const canDeleteFolders = hasRole(['sysadmin', 'manager']);
  const canManageFiles = hasRole(['sysadmin', 'manager', 'staff', 'client_admin', 'client_user']);
  const isStaff = hasRole(['sysadmin', 'manager', 'staff']);
  const isSysadmin = hasRole(['sysadmin']);
  const { orgId = '', folderId = '' } = useParams<{ orgId: string; folderId: string }>();
  const navigate = useNavigate();
  const workspaces = useWorkspaces(orgId);
  const workspaceId = workspaces.data?.[0]?.id ?? null;
  const [showArchived, setShowArchived] = useState(false);
  const folders = useFolders(workspaceId, showArchived);

  const [showTrash, setShowTrash] = useState(false);
  const files = useFiles(folderId, showTrash);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filesDropOver, setFilesDropOver] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const startUpload = useUIStore((s) => s.startUpload);
  const endUpload = useUIStore((s) => s.endUpload);
  const pushToast = useUIStore((s) => s.pushToast);
  const addUploadItem = useUIStore((s) => s.addUploadItem);
  const updateUploadItem = useUIStore((s) => s.updateUploadItem);

  const [fileFilter, setFileFilter] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLInputElement>(null);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; file: FileItem } | null>(null);
  const [ctxFolder, setCtxFolder] = useState<{ x: number; y: number; folder: Folder } | null>(null);
  const [folderInfoTarget, setFolderInfoTarget] = useState<Folder | null>(null);
  const [moveFolderTarget, setMoveFolderTarget] = useState<Folder | null>(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<Folder | null>(null);
  const deleteFolder = useDeleteFolder();
  const [moveOpen, setMoveOpen] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const bulkDelete = useBulkDeleteFiles();
  const bulkArchive = useBulkArchiveFiles();
  const [ctxRenameFile, setCtxRenameFile] = useState<FileItem | null>(null);
  const [ctxMoveFile, setCtxMoveFile] = useState<FileItem | null>(null);
  const softDelete = useSoftDeleteFile();
  const restore = useRestoreFile();

  const current = useMemo(
    () => folders.data?.find((f) => f.id === folderId) ?? null,
    [folders.data, folderId],
  );

  const childFolders = useMemo(
    () => (folders.data ?? []).filter((f) => f.parent_id === folderId && !f.is_archived),
    [folders.data, folderId],
  );

  const org = useOrganization(isStaff ? orgId : undefined);

  const handleUploadFiles = useCallback(
    async (fileList: FileList | File[] | null) => {
      if (!fileList || !folderId) return;
      const list = Array.from(fileList);
      if (list.length === 0) return;
      await Promise.allSettled(
        list.map(async (f) => {
          const itemId = addUploadItem({
            fileName: f.name,
            folderId,
            folderName: current?.name ?? '',
            status: 'waiting',
            percent: 0,
          });
          startUpload();
          try {
            await uploadFile(folderId, f, (progress) => {
              updateUploadItem(itemId, {
                status: progress.status === 'done' ? 'done' : progress.status === 'error' ? 'error' : 'uploading',
                percent: progress.percent,
              });
            });
            qc.invalidateQueries({ queryKey: ['files', folderId] });
          } catch (err) {
            updateUploadItem(itemId, { status: 'error', error: apiError(err) });
            pushToast({ title: `Upload failed: ${f.name}`, body: apiError(err), variant: 'error' });
          } finally {
            endUpload();
          }
        }),
      );
    },
    [folderId, current?.name, qc, addUploadItem, updateUploadItem, startUpload, endUpload, pushToast],
  );

  const createFolder = useCreateFolder();

  const handleDropUpload = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setFilesDropOver(false);
      if (!folderId || !workspaceId) return;
      // Ignore internal drag-and-drop (folder/file row reorders)
      if (e.dataTransfer.types.includes('text/plain')) return;

      const items = Array.from(e.dataTransfer.items);
      const entries = items
        .map((item) => item.webkitGetAsEntry?.())
        .filter((entry): entry is FileSystemEntry => !!entry);

      if (entries.length === 0) {
        await handleUploadFiles(e.dataTransfer.files);
        return;
      }

      const hasFolder = entries.some((entry) => entry.isDirectory);

      if (!hasFolder) {
        // Fast path: flat file drop — use existing flow
        await handleUploadFiles(e.dataTransfer.files);
        return;
      }

      const wsId = workspaceId; // capture for closure

      const processEntry = async (entry: FileSystemEntry, parentFolderId: string): Promise<void> => {
        if (entry.isFile) {
          const fileEntry = entry as FileSystemFileEntry;
          await new Promise<void>((resolve) => {
            fileEntry.file(async (file) => {
              const itemId = addUploadItem({
                fileName: file.name,
                folderId: parentFolderId,
                folderName: current?.name ?? '',
                status: 'waiting',
                percent: 0,
              });
              startUpload();
              try {
                await uploadFile(parentFolderId, file, (progress) => {
                  updateUploadItem(itemId, {
                    status: progress.status === 'done' ? 'done' : progress.status === 'error' ? 'error' : 'uploading',
                    percent: progress.percent,
                  });
                });
              } catch (err) {
                updateUploadItem(itemId, { status: 'error', error: apiError(err) });
                pushToast({ title: `Upload failed: ${file.name}`, body: apiError(err), variant: 'error' });
              } finally {
                endUpload();
                resolve();
              }
            });
          });
        } else if (entry.isDirectory) {
          const dirEntry = entry as FileSystemDirectoryEntry;
          let newFolderId: string;
          try {
            const created = await createFolder.mutateAsync({
              workspace_id: wsId,
              parent_id: parentFolderId,
              name: dirEntry.name,
            });
            newFolderId = created.id;
          } catch (err) {
            pushToast({ title: `Failed to create folder: ${dirEntry.name}`, body: apiError(err), variant: 'error' });
            return;
          }
          // readEntries returns up to 100 entries per call; loop until empty
          const reader = dirEntry.createReader();
          const allChildren: FileSystemEntry[] = [];
          let batch: FileSystemEntry[];
          do {
            batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
            allChildren.push(...batch);
          } while (batch.length > 0);

          await Promise.allSettled(allChildren.map((child) => processEntry(child, newFolderId)));
        }
      };

      await Promise.allSettled(entries.map((entry) => processEntry(entry, folderId)));
      qc.invalidateQueries({ queryKey: ['files', folderId] });
      qc.invalidateQueries({ queryKey: ['folders'] });
    },
    [folderId, workspaceId, current?.name, qc, addUploadItem, updateUploadItem, startUpload, endUpload, pushToast, createFolder, handleUploadFiles],
  );

  const moveFile = useMoveFile();
  const moveFolder = useMoveFolder();
  const reorderFolder = useReorderFolder();
  const toast = useToast();

  const [creatingParent, setCreatingParent] = useState<string | null | undefined>(undefined);
  const [newFolderName, setNewFolderName] = useState('');

  const submitCreate = async () => {
    if (!workspaceId || !newFolderName.trim()) return;
    try {
      await createFolder.mutateAsync({
        workspace_id: workspaceId,
        parent_id: creatingParent ?? null,
        name: newFolderName.trim(),
      });
      toast.success('Folder created');
      setCreatingParent(undefined);
      setNewFolderName('');
    } catch (e) {
      toast.error('Create failed', apiError(e));
    }
  };

  const folderActivity = useFolderActivity(folderInfoTarget?.id ?? null);
  const subscription = useFolderSubscription(folderId || null);
  const subscribe = useSubscribeFolder();
  const unsubscribe = useUnsubscribeFolder();
  const isSubscribed = subscription.data?.subscribed ?? false;
  const subscriptionPending =
    subscribe.isPending || unsubscribe.isPending || subscription.isLoading;

  const handleToggleSubscription = () => {
    if (!folderId) return;
    if (isSubscribed) {
      unsubscribe.mutate(
        { folder_id: folderId },
        {
          onSuccess: () => toast.success('Unsubscribed from folder'),
          onError: (e) => toast.error('Unsubscribe failed', apiError(e)),
        },
      );
    } else {
      subscribe.mutate(
        { folder_id: folderId },
        {
          onSuccess: () =>
            toast.success(
              'Subscribed',
              'You will be notified of new or changed files in this folder.',
            ),
          onError: (e) => toast.error('Subscribe failed', apiError(e)),
        },
      );
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Drop selection whenever the user navigates to a new folder or flips the
  // Files/Trash tab — those rows belong to a different list.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [folderId, showTrash]);

  // Escape global handler — closes context menu
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCtxMenu(null);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const displayFiles = useMemo(() => {
    if (!files.data) return [];
    if (showTrash) return files.data.filter((f) => f.is_deleted);
    return files.data;
  }, [files.data, showTrash]);

  const filteredFiles = useMemo(() => {
    if (!fileFilter.trim()) return displayFiles;
    const q = fileFilter.toLowerCase();
    return displayFiles.filter((f) => f.name.toLowerCase().includes(q));
  }, [displayFiles, fileFilter]);

  usePageTitle(current?.name ?? 'Folder');

  // Separate file IDs from folder IDs in the selection set
  const selectedFileIds = useMemo(
    () => new Set([...selectedIds].filter((id) => displayFiles.some((f) => f.id === id))),
    [selectedIds, displayFiles],
  );

  const handleDownloadSelected = async () => {
    if (selectedFileIds.size === 0) return;
    setZipBusy(true);
    try {
      await downloadFolderZip(folderId, [...selectedFileIds], current?.name ?? 'files');
      toast.success(`Downloading ${selectedFileIds.size} file${selectedFileIds.size === 1 ? '' : 's'} as ZIP`);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error('ZIP download failed', apiError(e));
    } finally {
      setZipBusy(false);
    }
  };

  const handleBulkDelete = () => {
    const deletableIds = [...selectedFileIds].filter((id) => {
      const file = displayFiles.find((f) => f.id === id);
      return file && !file.is_deleted;
    });
    if (deletableIds.length === 0) {
      toast.info('Nothing to delete');
      setConfirmBulkDelete(false);
      return;
    }
    bulkDelete.mutate(
      { file_ids: deletableIds, folder_id: folderId },
      {
        onSuccess: (result) => {
          toast.success(`Deleted ${result.deleted} file${result.deleted === 1 ? '' : 's'}`);
          setConfirmBulkDelete(false);
          setSelectedIds(new Set());
        },
        onError: (e) => toast.error('Delete failed', apiError(e)),
      },
    );
  };

  const handleArchiveSelected = async () => {
    const archivableIds = [...selectedFileIds].filter((id) => {
      const file = displayFiles.find((f) => f.id === id);
      return file && !file.is_deleted && !file.is_archived;
    });
    if (archivableIds.length === 0) {
      toast.info('Nothing to archive', 'Selected files are already archived or deleted.');
      return;
    }
    setArchiveBusy(true);
    try {
      const result = await bulkArchive.mutateAsync({ file_ids: archivableIds, folder_id: folderId, archive: true });
      toast.success(`Archived ${result.archived} file${result.archived === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error('Archive failed', apiError(e));
    } finally {
      setArchiveBusy(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || !workspaceId) return;

    const activeData = active.data.current as FileDragData | FolderDragData | undefined;
    const overData = over.data.current as
      | { type?: string; folderId?: string }
      | undefined;
    if (!activeData || !overData) return;

    const targetFolderId = overData.folderId;
    if (!targetFolderId) return;

    const folderList: Folder[] = folders.data ?? [];
    const targetFolder = folderList.find((f) => f.id === targetFolderId) ?? null;

    // --- File drop -------------------------------------------------------
    if (activeData.type === 'file') {
      if (activeData.currentFolderId === targetFolderId) return;
      moveFile.mutate(
        {
          id: activeData.fileId,
          folder_id: targetFolderId,
          prev_folder_id: activeData.currentFolderId,
        },
        {
          onSuccess: () =>
            toast.success(
              `Moved ${activeData.fileName} to ${targetFolder?.name ?? 'folder'}`,
            ),
          onError: (e) => toast.error('Move failed', apiError(e)),
        },
      );
      return;
    }

    // --- Folder drop -----------------------------------------------------
    if (activeData.type === 'folder') {
      if (activeData.folderId === targetFolderId) return;

      const draggedFolder = folderList.find((f) => f.id === activeData.folderId) ?? null;

      // Sortable reorder: dropped onto a sibling under the same parent.
      const sameParent =
        targetFolder &&
        draggedFolder &&
        targetFolder.parent_id === draggedFolder.parent_id;

      if (sameParent && targetFolder) {
        const targetSort = targetFolder.sort_order ?? 0;
        reorderFolder.mutate(
          {
            id: activeData.folderId,
            sort_order: targetSort,
            workspace_id: workspaceId,
          },
          {
            onSuccess: () => toast.success('Reordered'),
            onError: (e) => toast.error('Reorder failed', apiError(e)),
          },
        );
        return;
      }

      // Otherwise: nest the dragged folder under the drop target.
      moveFolder.mutate(
        {
          id: activeData.folderId,
          parent_id: targetFolderId,
          workspace_id: workspaceId,
        },
        {
          onSuccess: () =>
            toast.success(
              `Moved ${draggedFolder?.name ?? 'folder'} into ${targetFolder?.name ?? 'folder'}`,
            ),
          onError: (e) => toast.error('Move failed', apiError(e)),
        },
      );
    }
  };

  return (
    <AppShell
      crumbs={
        isStaff
          ? [
              { label: 'Organizations', to: '/admin/organizations' },
              { label: org.data?.name ?? orgId, to: `/workspace/${orgId}` },
              { label: current?.name ?? 'Folder' },
            ]
          : [
              { label: 'My files', to: `/workspace/${orgId}` },
              { label: current?.name ?? 'Folder' },
            ]
      }
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className={cn(
          'gap-4',
          isStaff
            ? 'grid grid-cols-1 lg:grid-cols-[300px_1fr]'
            : 'flex flex-col',
        )}>
          {/* Folder tree panel — visible for staff only */}
          {isStaff && (
            <Card title="Folders">
              {folders.isLoading ? (
                <Spinner />
              ) : (
                <FolderTree
                  orgId={orgId}
                  folders={folders.data ?? []}
                  currentFolderId={folderId}
                  workspaceId={workspaceId}
                  showArchived={showArchived}
                  onToggleArchived={setShowArchived}
                  onCreateChild={(pid) => setCreatingParent(pid)}
                />
              )}
            </Card>
          )}

          <div className="space-y-4">
            <Card>
              <FolderBreadcrumb orgId={orgId} folders={folders.data ?? []} current={current} />
            </Card>

            <Card
              onDragOver={(e: React.DragEvent) => {
                if (showTrash) return;
                e.preventDefault();
                if (!e.dataTransfer.types.includes('text/plain')) setFilesDropOver(true);
              }}
              onDragLeave={(e: React.DragEvent) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setFilesDropOver(false);
              }}
              onDrop={showTrash ? undefined : handleDropUpload}
              className={filesDropOver ? 'ring-2 ring-[#0410BD] ring-inset' : undefined}
              title={
                isClient ? (
                  'Files'
                ) : (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowTrash(false)}
                      className={
                        !showTrash
                          ? 'text-sm font-semibold text-bb-navy border-b-2 border-bb-navy pb-0.5'
                          : 'text-sm font-medium text-slate-500 hover:text-slate-700 pb-0.5'
                      }
                    >
                      Files
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTrash(true)}
                      className={
                        showTrash
                          ? 'text-sm font-semibold text-red-600 border-b-2 border-red-500 pb-0.5 inline-flex items-center gap-1'
                          : 'text-sm font-medium text-slate-500 hover:text-slate-700 pb-0.5 inline-flex items-center gap-1'
                      }
                    >
                      <Trash2 size={14} />
                      Trash
                    </button>
                  </div>
                )
              }
              padded={false}
            >
              {/* Toolbar */}
              <div className="border-b border-[#E3E3F1] dark:border-[#2A2A4A] px-4 py-2 flex flex-wrap items-center gap-2 min-h-[44px]">
                {/* Left: create actions */}
                {!showTrash && (
                  <>
                    <Button variant="secondary" onClick={() => uploadInputRef.current?.click()} className="text-xs gap-1.5">
                      <UploadCloud size={14} /> Upload
                    </Button>
                    <input ref={uploadInputRef} type="file" multiple className="hidden"
                      onChange={(e) => { handleUploadFiles(e.target.files); e.target.value = ''; }} />
                    {canManageFolders && (
                      <Button variant="secondary" onClick={() => setCreatingParent(folderId)} disabled={!workspaceId} className="text-xs gap-1.5">
                        <FolderPlus size={14} /> New folder
                      </Button>
                    )}
                  </>
                )}

                {/* Selection actions */}
                {selectedIds.size > 0 && (
                  <>
                    <span className="w-px h-4 bg-[#E3E3F1] mx-0.5 hidden sm:block" />
                    <span className="text-xs font-medium text-[#676687]">{selectedIds.size} selected</span>
                    {selectedFileIds.size > 0 && (
                    <Button variant="secondary" onClick={handleDownloadSelected} loading={zipBusy} disabled={zipBusy} className="text-xs gap-1.5">
                      <Download size={14} /> Download{selectedFileIds.size < selectedIds.size ? ` ${selectedFileIds.size} file${selectedFileIds.size === 1 ? '' : 's'}` : ''}
                    </Button>
                    )}
                    {canManageFiles && workspaceId && (
                      <Button variant="secondary" onClick={() => setMoveOpen(true)} className="text-xs gap-1.5">
                        <FolderInput size={14} /> Move
                      </Button>
                    )}
                    {canManageFiles && selectedFileIds.size > 0 && (
                      <Button variant="secondary" onClick={handleArchiveSelected} loading={archiveBusy} disabled={archiveBusy} className="text-xs gap-1.5">
                        <Archive size={14} /> Archive
                      </Button>
                    )}
                    {canManageFiles && selectedFileIds.size > 0 && (
                      <Button variant="danger" onClick={() => setConfirmBulkDelete(true)} className="text-xs gap-1.5">
                        <Trash2 size={14} /> Delete
                      </Button>
                    )}
                    <button onClick={() => setSelectedIds(new Set())} className="text-xs text-[#BABACE] hover:text-[#676687] inline-flex items-center gap-0.5 ml-1">
                      <XIcon size={11} /> Clear
                    </button>
                  </>
                )}

                {/* Spacer */}
                <div className="flex-1" />

                {/* Subscribe button (non-client) */}
                {!isClient && folderId && (
                  <Button
                    variant={isSubscribed ? 'primary' : 'secondary'}
                    onClick={handleToggleSubscription}
                    loading={subscribe.isPending || unsubscribe.isPending}
                    disabled={subscriptionPending}
                    className="text-xs gap-1.5"
                    title={isSubscribed ? 'Unsubscribe from folder' : 'Get notified of changes'}
                  >
                    {isSubscribed ? <><Bell size={14} /> Subscribed</> : <><BellOff size={14} /> Subscribe</>}
                  </Button>
                )}

                {/* Filter */}
                {filterOpen ? (
                  <div className="flex items-center gap-1.5 border border-[#E3E3F1] rounded-md px-2.5 py-1.5 bg-white">
                    <Search size={13} className="text-[#BABACE] shrink-0" />
                    <input
                      ref={filterRef}
                      value={fileFilter}
                      onChange={(e) => setFileFilter(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') { setFileFilter(''); setFilterOpen(false); }
                      }}
                      placeholder="Filter files…"
                      className="text-xs outline-none w-36 text-[#12122C] placeholder:text-[#BABACE] bg-transparent"
                      autoFocus
                    />
                    {fileFilter && (
                      <button onClick={() => setFileFilter('')} className="text-[#BABACE] hover:text-[#676687]">
                        <XIcon size={12} />
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setFilterOpen(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[#676687] border border-[#E3E3F1] rounded-md hover:bg-[#F2F2F8] transition-colors"
                    title="Filter files"
                  >
                    <Search size={13} />
                  </button>
                )}
              </div>

              {/* File table */}
              <div className={`p-4 flex flex-col min-h-[calc(100vh-280px)] ${filesDropOver ? 'bg-[#EFF0FF]/60 dark:bg-[#1E2040]/40' : ''}`}>
                {showTrash && (
                  <p className="text-xs text-slate-500 mb-3">
                    Files deleted from this folder appear here. Use the Restore action to bring one back.
                  </p>
                )}
                {filesDropOver && !showTrash && (
                  <div className="flex-1 flex items-center justify-center border-2 border-dashed border-[#0410BD] rounded-lg mb-3 py-8">
                    <div className="text-center text-[#0410BD]">
                      <UploadCloud size={32} className="mx-auto mb-2 opacity-70" />
                      <p className="text-sm font-medium">Drop to upload</p>
                    </div>
                  </div>
                )}
                {!filesDropOver && (
                  files.isLoading ? (
                    <div className="flex justify-center py-6">
                      <Spinner />
                    </div>
                  ) : files.isError ? (
                    <div className="text-sm text-red-600">{apiError(files.error)}</div>
                  ) : (
                    <FileTable
                      files={filteredFiles}
                      folders={showTrash ? [] : childFolders}
                      orgId={orgId}
                      workspaceId={workspaceId ?? undefined}
                      selectedIds={selectedIds}
                      onSelectionChange={setSelectedIds}
                      canManageFiles={canManageFiles}
                      canDeleteFolders={canDeleteFolders}
                      onContextMenu={(e, file) => {
                        e.preventDefault();
                        setCtxMenu({ x: e.clientX, y: e.clientY, file });
                      }}
                      onFolderContextMenu={(e, folder) => {
                        e.preventDefault();
                        setCtxFolder({ x: e.clientX, y: e.clientY, folder });
                      }}
                      onFolderAction={(action, folder) => {
                        if (action === 'open') navigate(`/workspace/${orgId}/folders/${folder.id}`);
                        else if (action === 'info') setFolderInfoTarget(folder);
                        else if (action === 'move') setMoveFolderTarget(folder);
                        else if (action === 'delete') setDeleteFolderTarget(folder);
                      }}
                    />
                  )
                )}
              </div>
            </Card>
          </div>
        </div>
      </DndContext>

      {/* Context menu */}
      {ctxMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setCtxMenu(null)} />
          <div
            className="fixed z-50 bg-white dark:bg-[#1A1A2E] border border-[#E3E3F1] dark:border-[#2A2A4A] rounded-lg shadow-xl py-1 min-w-[160px] text-sm"
            style={{ left: ctxMenu.x, top: ctxMenu.y }}
          >
            <button className="w-full text-left px-3 py-2 hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] text-[#12122C] dark:text-[#C4C4DC]" onClick={() => { navigate(`/files/${ctxMenu.file.id}`); setCtxMenu(null); }}>Details</button>
            <button className="w-full text-left px-3 py-2 hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] text-[#12122C] dark:text-[#C4C4DC]" onClick={() => { downloadFile(ctxMenu.file.id, ctxMenu.file.name); setCtxMenu(null); }}>Download</button>
            {canManageFiles && !ctxMenu.file.is_deleted && (
              <>
                <div className="my-1 border-t border-[#E3E3F1] dark:border-[#2A2A4A]" />
                <button className="w-full text-left px-3 py-2 hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] text-[#12122C] dark:text-[#C4C4DC]" onClick={() => { setCtxRenameFile(ctxMenu.file); setCtxMenu(null); }}>Rename</button>
                {workspaceId && <button className="w-full text-left px-3 py-2 hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] text-[#12122C] dark:text-[#C4C4DC]" onClick={() => { setCtxMoveFile(ctxMenu.file); setCtxMenu(null); }}>Move to folder</button>}
                <button className="w-full text-left px-3 py-2 hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] text-red-600" onClick={() => {
                  const f = ctxMenu.file;
                  setCtxMenu(null);
                  softDelete.mutate(
                    { id: f.id, folder_id: f.folder_id },
                    {
                      onSuccess: () => toast.success(`Deleted ${f.name}`, {
                        action: {
                          label: 'Undo',
                          onClick: async () => {
                            restore.mutate(
                              { id: f.id, folder_id: f.folder_id },
                              { onError: (e) => toast.error('Restore failed', apiError(e)) },
                            );
                          },
                        },
                        durationMs: 6000,
                      }),
                      onError: (e) => toast.error('Delete failed', apiError(e)),
                    },
                  );
                }}>Delete</button>
              </>
            )}
          </div>
        </>
      )}

      {/* Folder context menu */}
      {ctxFolder && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setCtxFolder(null)} />
          <div
            className="fixed z-50 bg-white dark:bg-[#1A1A2E] border border-[#E3E3F1] dark:border-[#2A2A4A] rounded-lg shadow-xl py-1 min-w-[160px] text-sm"
            style={{ left: ctxFolder.x, top: ctxFolder.y }}
          >
            <button className="w-full text-left px-3 py-2 hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] text-[#12122C] dark:text-[#C4C4DC]"
              onClick={() => { navigate(`/workspace/${orgId}/folders/${ctxFolder.folder.id}`); setCtxFolder(null); }}>
              Open
            </button>
            <button className="w-full text-left px-3 py-2 hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] text-[#12122C] dark:text-[#C4C4DC]"
              onClick={() => { setFolderInfoTarget(ctxFolder.folder); setCtxFolder(null); }}>
              Info
            </button>
            {canManageFolders && workspaceId && (
              <button className="w-full text-left px-3 py-2 hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] text-[#12122C] dark:text-[#C4C4DC]"
                onClick={() => { setMoveFolderTarget(ctxFolder.folder); setCtxFolder(null); }}>
                Move to folder
              </button>
            )}
            {canDeleteFolders && (
              <>
                <div className="my-1 border-t border-[#E3E3F1] dark:border-[#2A2A4A]" />
                <button className="w-full text-left px-3 py-2 hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] text-red-600"
                  onClick={() => { setDeleteFolderTarget(ctxFolder.folder); setCtxFolder(null); }}>
                  Delete
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* Folder info modal */}
      <Modal
        open={!!folderInfoTarget}
        onClose={() => setFolderInfoTarget(null)}
        title="Folder info"
        footer={<Button variant="secondary" onClick={() => setFolderInfoTarget(null)}>Close</Button>}
      >
        {folderInfoTarget && (
          <>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-[#676687] font-medium">Name</dt>
              <dd className="text-[#12122C] dark:text-[#C4C4DC] break-all">{folderInfoTarget.name}</dd>
              <dt className="text-[#676687] font-medium">Path</dt>
              <dd className="text-[#12122C] dark:text-[#C4C4DC] break-all font-mono text-xs">{folderInfoTarget.path}</dd>
              <dt className="text-[#676687] font-medium">Created by</dt>
              <dd className="text-[#12122C] dark:text-[#C4C4DC]">{folderInfoTarget.created_by_name ?? '—'}</dd>
              <dt className="text-[#676687] font-medium">Created</dt>
              <dd className="text-[#12122C] dark:text-[#C4C4DC]">{new Date(folderInfoTarget.created_at).toLocaleString()}</dd>
              <dt className="text-[#676687] font-medium">Modified</dt>
              <dd className="text-[#12122C] dark:text-[#C4C4DC]">{new Date(folderInfoTarget.updated_at).toLocaleString()}</dd>
              <dt className="text-[#676687] font-medium">ID</dt>
              <dd className="text-[#12122C] dark:text-[#C4C4DC] font-mono text-xs break-all">{folderInfoTarget.id}</dd>
            </dl>
            <div className="mt-4 border-t border-[#E3E3F1] dark:border-[#2A2A4A] pt-3">
              <p className="text-xs font-semibold text-[#676687] uppercase tracking-wide mb-2">Activity</p>
              {folderActivity.isLoading ? (
                <div className="flex justify-center py-3"><Spinner /></div>
              ) : (folderActivity.data?.items ?? []).length === 0 ? (
                <p className="text-xs text-[#BABACE]">No activity recorded.</p>
              ) : (
                <ul className="space-y-1.5 max-h-48 overflow-y-auto custom-scrollbar">
                  {(folderActivity.data?.items ?? []).map((entry) => (
                    <li key={entry.id} className="text-xs text-[#676687] flex items-start gap-2">
                      <span className="text-[#BABACE] whitespace-nowrap shrink-0">
                        {new Date(entry.created_at).toLocaleString()}
                      </span>
                      <span>
                        <span className="font-medium text-[#12122C] dark:text-[#C4C4DC]">{(entry as { actor_name?: string | null }).actor_name ?? 'System'}</span>
                        {' '}{entry.action.replace('folder.', '')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Move folder modal */}
      {moveFolderTarget && workspaceId && (
        <MoveFolderModal
          open
          folder={moveFolderTarget}
          workspaceId={workspaceId}
          allFolders={folders.data ?? []}
          onClose={() => setMoveFolderTarget(null)}
        />
      )}

      {/* Delete folder confirm */}
      <Modal
        open={!!deleteFolderTarget}
        onClose={() => setDeleteFolderTarget(null)}
        title={`Delete "${deleteFolderTarget?.name}"?`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteFolderTarget(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={deleteFolder.isPending}
              onClick={() => {
                if (!deleteFolderTarget || !workspaceId) return;
                deleteFolder.mutate(
                  { id: deleteFolderTarget.id, workspace_id: workspaceId },
                  {
                    onSuccess: () => {
                      toast.success(`Deleted "${deleteFolderTarget.name}"`);
                      setDeleteFolderTarget(null);
                    },
                    onError: (e) => toast.error('Delete failed', apiError(e)),
                  },
                );
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        {isSysadmin ? (
          <p className="text-sm text-slate-700">
            This will permanently delete the folder and all its contents. This cannot be undone.
          </p>
        ) : (
          <p className="text-sm text-slate-700">
            The folder must be empty before it can be deleted. Delete or move all files and subfolders first, then delete this folder.
          </p>
        )}
      </Modal>

      {/* Bulk move modal */}
      {workspaceId && (
        <BulkMoveModal
          open={moveOpen}
          onClose={() => setMoveOpen(false)}
          fileIds={[...selectedFileIds].filter(id => {
            const f = displayFiles.find(x => x.id === id);
            return f && !f.is_deleted;
          })}
          currentFolderId={folderId}
          workspaceId={workspaceId}
          onMoved={() => { setMoveOpen(false); setSelectedIds(new Set()); }}
        />
      )}

      {/* Bulk delete confirm modal */}
      <Modal
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        title={`Delete ${selectedFileIds.size} file${selectedFileIds.size === 1 ? '' : 's'}?`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmBulkDelete(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleBulkDelete} loading={bulkDelete.isPending}>Delete</Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">This will move the selected files to Trash. You can restore them later.</p>
      </Modal>

      {/* Context rename/move modals */}
      {ctxRenameFile && (
        <RenameFileModal open file={ctxRenameFile} onClose={() => setCtxRenameFile(null)} />
      )}
      {ctxMoveFile && workspaceId && (
        <MoveFileModal open file={ctxMoveFile} workspaceId={workspaceId} onClose={() => setCtxMoveFile(null)} />
      )}

      {/* New folder modal */}
      <Modal
        open={creatingParent !== undefined}
        onClose={() => {
          setCreatingParent(undefined);
          setNewFolderName('');
        }}
        title="New folder"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setCreatingParent(undefined);
                setNewFolderName('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitCreate}
              loading={createFolder.isPending}
              disabled={!newFolderName.trim()}
              className="w-auto"
            >
              Create
            </Button>
          </>
        }
      >
        <Label htmlFor="subfolder-name">Name</Label>
        <Input
          id="subfolder-name"
          autoFocus
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          placeholder="e.g. Q1 Receipts"
        />
      </Modal>
    </AppShell>
  );
}

function MoveFolderModal({
  open,
  folder,
  workspaceId,
  allFolders,
  onClose,
}: {
  open: boolean;
  folder: Folder;
  workspaceId: string;
  allFolders: Folder[];
  onClose: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const move = useMoveFolder();
  const toast = useToast();

  // Can't move a folder into itself or its own children
  const getDescendantIds = (id: string): Set<string> => {
    const ids = new Set<string>();
    const collect = (parentId: string) => {
      allFolders.filter((f) => f.parent_id === parentId).forEach((f) => {
        ids.add(f.id);
        collect(f.id);
      });
    };
    collect(id);
    return ids;
  };
  const forbidden = new Set([folder.id, ...getDescendantIds(folder.id)]);
  const available = allFolders.filter((f) => !forbidden.has(f.id) && f.id !== folder.parent_id);

  const handleSubmit = () => {
    if (!selectedId) return;
    move.mutate(
      { id: folder.id, parent_id: selectedId, workspace_id: workspaceId },
      {
        onSuccess: () => { toast.success('Folder moved'); onClose(); },
        onError: (e) => toast.error('Move failed', apiError(e)),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Move "${folder.name}"`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={move.isPending} disabled={!selectedId}>Move</Button>
        </>
      }
    >
      <p className="text-sm text-slate-600 mb-3">Select a destination folder.</p>
      {available.length === 0 ? (
        <p className="text-sm text-slate-500">No other folders available.</p>
      ) : (
        <ul className="border border-[#E3E3F1] divide-y divide-slate-100 max-h-60 overflow-y-auto custom-scrollbar">
          {available.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => setSelectedId(f.id)}
                className={cn('w-full text-left px-3 py-2 text-sm hover:bg-slate-50', selectedId === f.id ? 'bg-bb-navy/5 text-bb-navy font-medium' : 'text-slate-700')}
              >
                {f.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
